import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/MarketingLayout";
import Seo from "@/components/Seo";
import { PriceCalculator } from "@/components/PriceCalculator";
import api from "@/lib/api";
import { ArrowRight, Check, Star, ArrowUp, PlusCircle } from "@phosphor-icons/react";

const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB")}`;

const PRICING_DESC =
  "Simple per-organisation pricing: £39 per connected Xero organisation per month, with volume discounts up to 69% and 2 months free when you pay annually. Start a free 7-day trial.";

const PricingHeader = () => (
  <section className="max-w-3xl mx-auto px-6 pt-16 pb-8 text-center">
    <div className="overline mb-4" style={{ color: "#F37A24" }}>Pricing</div>
    <h1 className="font-heading font-black text-navy text-4xl sm:text-5xl tracking-tighter">
      Simple pricing that grows with your practice
    </h1>
    <p className="text-navy/70 text-base sm:text-lg mt-5 leading-relaxed">
      Everything you need to automate month-end. One subscription. Every feature included.
      No hidden modules. No per-user charges. Whether you manage one company or fifty,
      Lottie grows with your practice.
    </p>
  </section>
);

export default function Pricing() {
  const [cfg, setCfg] = useState(null);
  const [annual, setAnnual] = useState(false);

  useEffect(() => { api.get("/billing/plans").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  if (!cfg) return (
    <MarketingLayout>
      <Seo title="Pricing" path="/pricing" description={PRICING_DESC} />
      <PricingHeader />
      <div className="h-96" data-testid="pricing-loading" />
    </MarketingLayout>
  );

  const gp = cfg.growth_pack;

  return (
    <MarketingLayout>
      <Seo title="Pricing" path="/pricing" description={PRICING_DESC} />
      <PricingHeader />
      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3 mb-10" data-testid="billing-toggle">
        <span className={`text-sm font-medium ${annual ? "text-navy/65" : "text-navy"}`}>Monthly</span>
        <button onClick={() => setAnnual(!annual)} data-testid="billing-cycle-toggle"
          className="relative w-12 h-6 rounded-full transition-colors" style={{ background: annual ? "#F37A24" : "#cbd5e1" }}>
          <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform" style={{ transform: annual ? "translateX(24px)" : "none" }} />
        </button>
        <span className={`text-sm font-medium ${annual ? "text-navy" : "text-navy/65"}`}>Annual</span>
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "rgba(0,168,89,0.12)", color: "#00A859" }}>
          Save 2 months
        </span>
      </div>

      {/* Plan grid */}
      <section className="max-w-6xl mx-auto px-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cfg.plans.map((p) => {
            const featured = p.featured;
            const price = annual ? p.annual : p.monthly;
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`}
                className={`relative rounded-2xl border bg-white p-7 flex flex-col ${featured ? "border-[#F37A24] shadow-card-hover ring-1 ring-[#F37A24]" : "border-slate-200 shadow-card"}`}>
                {featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 text-white text-xs font-bold px-3 py-1 rounded-full" style={{ background: "#F37A24" }}>
                    <Star size={12} weight="fill" /> Most Popular
                  </div>
                )}
                <h3 className="font-heading font-bold text-navy text-xl">{p.name}</h3>
                <p className="text-navy/70 text-sm mt-1">
                  {p.enterprise ? "51+ companies" : p.companies === 1 ? "1 company" : `Up to ${p.companies} companies`}
                </p>
                <div className="mt-5 mb-6">
                  {p.enterprise ? (
                    <div className="font-heading font-black text-navy text-3xl">Contact Us</div>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span data-testid={`plan-price-${p.id}`} className="font-heading font-black text-navy text-4xl">{gbp(price)}</span>
                      <span className="text-navy/70 text-sm">/{annual ? "year" : "month"}</span>
                    </div>
                  )}
                  <div className="text-navy/65 text-xs mt-1">{p.enterprise ? "Tailored to your business" : "excl. VAT"}</div>
                  {!p.enterprise && p.savings_pct > 0 && (
                    <div className="mt-3" data-testid={`plan-volume-save-${p.id}`}>
                      <div className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full"
                        style={{ background: "rgba(14,15,63,0.06)", color: "#0E0F3F" }}>
                        Up to {p.savings_pct}% cheaper per company
                      </div>
                      <div className="text-navy/65 text-[0.7rem] mt-1.5 leading-snug" data-testid={`plan-volume-working-${p.id}`}>
                        {p.companies} × £{cfg.base_per_company} = {gbp(p.list_monthly)}/mo at list —
                        you pay {gbp(p.monthly)}/mo (save {gbp(p.list_monthly - p.monthly)})
                      </div>
                    </div>
                  )}
                  {!p.enterprise && annual && p.monthly > 0 && (
                    <div className="inline-flex items-center gap-1 text-xs font-semibold mt-2 px-2 py-0.5 rounded-full" data-testid={`plan-save-${p.id}`}
                      style={{ background: "rgba(0,168,89,0.12)", color: "#00A859" }}>
                      Save {gbp(p.monthly * 12 - p.annual)}/yr · 2 months free
                    </div>
                  )}
                  {!p.enterprise && !annual && p.monthly > 0 && (
                    <div className="text-navy/65 text-xs mt-2" data-testid={`plan-annual-hint-${p.id}`}>
                      or {gbp(p.annual)}/yr — 2 months free
                    </div>
                  )}
                </div>

                {p.enterprise ? (
                  <a href="mailto:hello@workwithlottie.com?subject=Enterprise%20enquiry" data-testid="enterprise-contact"
                    className="mt-auto text-center font-semibold py-3 rounded-xl border border-navy text-navy hover:bg-navy hover:text-white transition-colors">
                    Contact Sales
                  </a>
                ) : (
                  <Link to={`/start?plan=${p.id}`} data-testid={`plan-cta-${p.id}`}
                    className="mt-auto text-center font-semibold py-3 rounded-xl transition-colors text-white hover:opacity-90"
                    style={{ background: "#F37A24" }}>
                    Start free trial
                  </Link>
                )}

                {/* Growth pack note (Team upwards) */}
                {p.growth_pack && (
                  <div className="mt-4 rounded-xl p-3 text-xs leading-relaxed" style={{ background: "rgba(244,122,36,0.07)" }} data-testid={`growth-note-${p.id}`}>
                    <span className="font-semibold text-navy">Need more capacity?</span>{" "}
                    <span className="text-navy/65">Add a {gp.companies}-Company Growth Pack for {gbp(annual ? gp.annual : gp.monthly)}/{annual ? "yr" : "mo"}, or upgrade whenever you're ready.</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {annual && <p className="text-center text-navy/70 text-sm mt-6">Save 2 months when you pay annually.</p>}
      </section>

      {/* Interactive price calculator — "not sure which package?" */}
      <section className="max-w-2xl mx-auto px-6 mt-16">
        <div className="text-center mb-6">
          <div className="overline mb-2" style={{ color: "#F37A24" }}>Instant quote</div>
          <h2 className="font-heading font-bold text-navy text-2xl sm:text-3xl tracking-tight" data-testid="pricing-calculator-heading">
            Not sure which package is best for you?
          </h2>
          <p className="text-navy/70 mt-3">
            Tell us how many companies you manage and we'll show you the right plan and your exact price.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white shadow-card p-7" data-testid="pricing-calculator-card">
          <PriceCalculator />
        </div>
      </section>

      {/* Everything included */}
      <section className="max-w-4xl mx-auto px-6 mt-20 text-center">
        <h2 className="font-heading font-bold text-navy text-2xl sm:text-3xl tracking-tight">Everything included</h2>
        <p className="text-navy/70 mt-3">No hidden modules. No per-user charges. Every plan gets the full Lottie feature set.</p>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3 mt-10 text-left">
          {cfg.included_features.map((f) => (
            <div key={f} className="flex items-center gap-2.5" data-testid={`included-${f.slice(0,10)}`}>
              <Check size={18} weight="bold" className="text-positive shrink-0" />
              <span className="text-navy/80 text-sm">{f}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ROI — pays for itself */}
      <section className="max-w-4xl mx-auto px-6 mt-20">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-card p-8 text-center" data-testid="roi-section">
          <div className="overline mb-3" style={{ color: "#F37A24" }}>The maths</div>
          <h2 className="font-heading font-bold text-navy text-2xl sm:text-3xl tracking-tight">
            It pays for itself in the first month-end
          </h2>
          <p className="text-navy/70 mt-3 max-w-2xl mx-auto">
            Lottie replaces hours of manual accrual, prepayment and deferred-income admin every close — for
            less than the cost of a coffee a week per company.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 mt-8 text-left">
            <div data-testid="roi-per-entity">
              <div className="font-heading font-black text-navy text-3xl">£39<span className="text-base text-navy/70">/mo</span></div>
              <div className="text-navy/70 text-sm mt-1">Per Xero company — every feature included, less as you scale</div>
            </div>
            <div data-testid="roi-per-day">
              <div className="font-heading font-black text-navy text-3xl">~£1.30<span className="text-base text-navy/70">/day</span></div>
              <div className="text-navy/70 text-sm mt-1">Less than a daily coffee, per connected company</div>
            </div>
            <div data-testid="roi-hours">
              <div className="font-heading font-black text-navy text-3xl">Hours<span className="text-base text-navy/70"> back</span></div>
              <div className="text-navy/70 text-sm mt-1">Reclaim the repetitive month-end admin, every single period</div>
            </div>
          </div>
          <p className="text-navy/65 text-xs mt-6">
            Illustrative example based on £39/company/month. Actual time savings vary by the number of companies
            and schedules you run.
          </p>
        </div>
      </section>

      {/* Reassurance footer */}
      <section className="max-w-4xl mx-auto px-6 mt-16">
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
          {cfg.reassurance.map((r) => (
            <span key={r} className="flex items-center gap-2 text-navy font-medium" data-testid={`reassure-${r.slice(0,8)}`}>
              <Check size={18} weight="bold" className="text-positive" /> {r}
            </span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 py-20 text-center">
        <Link to="/start" data-testid="pricing-bottom-cta"
          className="inline-flex items-center gap-2 text-white font-semibold px-7 py-3.5 rounded-xl transition-transform hover:scale-[1.02]" style={{ background: "#F37A24" }}>
          Start your 7-day free trial <ArrowRight size={18} weight="bold" />
        </Link>
        <div className="text-navy/65 text-sm mt-3">No card required · Cancel anytime</div>
      </section>
    </MarketingLayout>
  );
}
